import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAppFilters } from "../state/filters";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Textarea } from "../components/ui/textarea";
import { SeverityPill, StatusPill } from "../components/StatusPills";
import DateRangePicker from "../components/DateRangePicker";
import { readableStatus, formatMoney } from "../lib/format";
import {
  SplitSquareVertical,
  CheckCircle2,
  XCircle,
  NotebookPen,
  ArrowUpDown,
  RefreshCw,
  DollarSign,
  Users,
  CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import ExportButton from "../components/ExportButton";

function DiffField({ label, a, b }) {
  const changed = (a || "") !== (b || "") && a !== undefined && b !== undefined;
  const missing = !a || !b;
  const tone = changed
    ? (missing
      ? "bg-[hsl(var(--negative))]/10 border-[hsl(var(--negative))]/30"
      : "bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30")
    : "";
  return (
    <div className={`border border-border rounded-md p-2 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="grid grid-cols-2 gap-2 text-xs mt-1">
        <div className="truncate" title={a}>{a ?? <span className="text-muted-foreground italic">missing</span>}</div>
        <div className="truncate text-right" title={b}>{b ?? <span className="text-muted-foreground italic">missing</span>}</div>
      </div>
    </div>
  );
}

function ActivityBadge({ kind }) {
  if (!kind) return <span className="text-[10px] text-muted-foreground italic">—</span>;
  const cfg = {
    sale: { icon: DollarSign, label: "Sale", cls: "bg-[hsl(var(--positive))]/10 text-[hsl(var(--positive))] border-[hsl(var(--positive))]/30" },
    lead_with_appt: { icon: CalendarClock, label: "Lead + appt", cls: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/30" },
    lead: { icon: Users, label: "Lead", cls: "bg-[hsl(var(--surface-3))] text-foreground border-border" },
    data: { icon: NotebookPen, label: "Data", cls: "bg-[hsl(var(--surface-3))] text-muted-foreground border-border" },
  }[kind] || { icon: NotebookPen, label: kind, cls: "bg-[hsl(var(--surface-3))] text-muted-foreground border-border" };
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium border ${cfg.cls}`}
      data-testid={`recon-activity-${kind}`}
    >
      <Icon size={10} />{cfg.label}
    </span>
  );
}

function formatActivityDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso.replace(" ", "T"));
    if (isNaN(d.getTime())) return iso.slice(0, 10);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  } catch { return String(iso).slice(0, 10); }
}

export default function Reconciliation() {
  const { range } = useAppFilters();
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "open",
    severity: "all",
    type: "all",
    sort_by: "recent_activity",
    exclude_massey: false,
  });
  const [selected, setSelected] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [checked, setChecked] = useState(new Set());
  const [resyncing, setResyncing] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const dateParams = { start: range.start, end: range.end };
      if (filters.exclude_massey) dateParams.exclude_massey = true;
      const params = {
        sort_by: filters.sort_by,
        limit: 500,
        ...dateParams,
      };
      if (filters.status !== "open") params.status = filters.status;
      if (filters.severity !== "all") params.severity = filters.severity;
      if (filters.type !== "all") params.type = filters.type;
      const [s, c, l] = await Promise.all([
        api.get("/reconciliation/summary", { params: dateParams }),
        api.get("/reconciliation/categories", { params: dateParams }),
        api.get("/reconciliation/list", { params }),
      ]);
      setSummary(s.data);
      setCategories(c.data.items || []);
      setItems(l.data.items || []);
      setTotal(l.data.total || 0);
      setChecked(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filters, range]);

  const updateStatus = async (id, status) => {
    try {
      await api.post(`/reconciliation/${id}/status`, { status });
      toast.success(`Marked ${readableStatus(status)}`);
      if (selected?.id === id) setSelected({ ...selected, status });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const markDoneVerified = async (id, { force = false } = {}) => {
    try {
      const { data } = await api.post(`/reconciliation/${id}/verify_done`, null, { params: { force } });
      if (data.ok) {
        if (data.verified) {
          toast.success("Verified resolved on both systems — marked done");
        } else if (data.already_resolved) {
          toast.message("Already resolved");
        } else {
          toast.success("Marked done");
        }
        if (selected?.id === id) setSelected({ ...selected, status: "resolved" });
        load();
      } else if (data.still_present) {
        // Issue still present after fresh pull — offer to force
        toast.warning(
          `Issue still detected after refetch: ${(data.reasons || []).join(" · ") || data.type}`,
          {
            duration: 8000,
            action: {
              label: "Force mark done",
              onClick: () => markDoneVerified(id, { force: true }),
            },
          }
        );
      }
    } catch (e) {
      // Fallback to simple status update if verify endpoint unreachable
      toast.error(`Verify failed: ${e?.response?.data?.detail || e.message}. Falling back to simple mark done.`);
      await updateStatus(id, "resolved");
    }
  };

  const bulkMarkDone = async (newStatus) => {
    const ids = Array.from(checked);
    if (!ids.length) return toast.message("Select one or more rows first");
    setBulkBusy(true);
    try {
      await api.post("/reconciliation/bulk_status", { ids, status: newStatus });
      toast.success(`${ids.length} marked ${readableStatus(newStatus)}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBulkBusy(false); }
  };

  const resync = async (full = false) => {
    setResyncing(true);
    try {
      const { data } = await api.post("/reconciliation/resync", null, { params: { full } });
      if (data.already_running) {
        toast.message("Refresh already in progress");
      } else {
        toast.success("Refreshing — pulling latest GHL + FieldRoutes…");
      }
      // Poll until done
      let pollTimer = null;
      const poll = async () => {
        try {
          const s = await api.get("/sync/status");
          if (!s.data?.any_running) {
            await load();
            setResyncing(false);
            // Surface the latest reconciliation log so user can see what changed
            try {
              const logs = await api.get("/sync/logs", { params: { limit: 5 } });
              const recon = (logs.data?.logs || []).find((l) => l.source === "reconciliation");
              if (recon) {
                const auto = recon.auto_resolved || 0;
                const newOnes = recon.new_discrepancies || 0;
                const inherited = recon.inherited_status || 0;
                const matchPct = recon.match_pct;
                const tail = inherited ? ` · ${inherited} kept prior status` : "";
                toast.success(
                  `Refresh done · ${matchPct ?? 0}% match · ${newOnes} new · ${auto} auto-resolved${tail}`
                );
              }
            } catch { /* ignore */ }
            if (pollTimer) clearInterval(pollTimer);
          }
        } catch { /* ignore */ }
      };
      pollTimer = setInterval(poll, 4000);
      setTimeout(() => {
        if (pollTimer) clearInterval(pollTimer);
        setResyncing(false);
        load();
      }, 180000);
    } catch (e) {
      toast.error(e.message);
      setResyncing(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selected) return;
    try {
      const { data } = await api.post(`/reconciliation/${selected.id}/note`, { text: noteText });
      setSelected({ ...selected, notes: [...(selected.notes || []), data.note] });
      setNoteText("");
      toast.success("Note added");
    } catch (e) { toast.error(e.message); }
  };

  const chips = useMemo(() => {
    const sorted = [...(categories || [])].sort((a, b) => b.count - a.count);
    const allCount = sorted.reduce((sum, c) => sum + (c.count || 0), 0);
    return [{ type: "all", label: "All categories", count: allCount }, ...sorted];
  }, [categories]);

  const allChecked = items.length > 0 && items.every((d) => checked.has(d.id));
  const toggleAll = () => {
    if (allChecked) setChecked(new Set());
    else setChecked(new Set(items.map((d) => d.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setChecked(next);
  };

  return (
    <div className="space-y-6" data-testid="page-reconciliation">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Reconciliation Center</h1>
          <p className="text-sm text-muted-foreground">
            Your to-do list of cross-system mismatches. Defaults to <b className="text-foreground">recent activity</b> — most recent sales first, then leads with appointments.
          </p>
          {summary?.cutoff && (
            <p className="text-[11px] text-muted-foreground mt-1" data-testid="recon-cutoff-label">
              Showing errors from <span className="text-foreground font-medium">{new Date(summary.cutoff).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span> onward
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-[hsl(var(--surface-2))]/40 text-xs" data-testid="recon-massey-switch-wrap">
            <Switch
              checked={filters.exclude_massey}
              onCheckedChange={(v) => setFilters({ ...filters, exclude_massey: v })}
              data-testid="recon-exclude-massey"
              className="data-[state=checked]:bg-[hsl(var(--positive))]"
            />
            <span className="whitespace-nowrap">Hide Massey leads</span>
          </label>
          <DateRangePicker />
          <Button
            variant="outline"
            size="sm"
            onClick={() => resync(true)}
            disabled={resyncing}
            data-testid="recon-refresh-btn"
            title="Pull latest GHL + FieldRoutes data, then re-run reconciliation"
          >
            <RefreshCw size={14} className={`mr-2 ${resyncing ? "animate-spin" : ""}`} />
            {resyncing ? "Refreshing…" : "Refresh data"}
          </Button>
          <ExportButton
            path="/export/reconciliation.csv"
            params={{
              ...(filters.status !== "open" ? { status: filters.status } : {}),
              ...(filters.severity !== "all" ? { severity: filters.severity } : {}),
              ...(filters.type !== "all" ? { type: filters.type } : {}),
            }}
            filename={`reconciliation_${new Date().toISOString().slice(0, 10)}.csv`}
            testid="recon-export-csv"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="recon-kpi-cards">
        {[
          { label: "Customers affected", value: summary?.open_customers || 0, hint: "Unique customers with open issues" },
          { label: "All issues", value: summary?.open || 0, hint: "Total open discrepancy rows" },
          { label: "High", value: summary?.by_severity?.high || 0, hint: "High severity" },
          { label: "Medium", value: summary?.by_severity?.medium || 0, hint: "Medium severity" },
        ].map((c) => (
          <Card key={c.label}>
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{c.label}</div>
              <div className="kpi-card-number text-2xl mt-1 tabular-nums">{c.value.toLocaleString()}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{c.hint}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Categories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2" data-testid="recon-category-chips">
            {chips.map((c) => {
              const active = filters.type === c.type;
              return (
                <button
                  key={c.type}
                  onClick={() => setFilters({ ...filters, type: c.type })}
                  className={`px-3 py-1.5 rounded-full text-xs border transition ${
                    active
                      ? "bg-[hsl(var(--surface-2))] text-foreground border-[hsl(var(--focus))]/60"
                      : "bg-[hsl(var(--surface-2))]/40 text-muted-foreground border-border hover:text-foreground"
                  }`}
                  data-testid={`recon-chip-${c.type}`}
                >
                  <span className="capitalize">{c.label}</span>
                  <span className={`ml-2 tabular-nums ${active ? "text-foreground" : "text-muted-foreground"}`}>
                    {(c.count || 0).toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <SplitSquareVertical size={16} /> Discrepancy queue
            </CardTitle>
            <Badge variant="secondary" className="tabular-nums" data-testid="recon-visible-count">
              {total.toLocaleString()} in window
            </Badge>
            {checked.size > 0 && (
              <Badge className="bg-[hsl(var(--focus))]/20 text-foreground border border-[hsl(var(--focus))]/40">
                {checked.size} selected
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {checked.size > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={bulkBusy}
                  onClick={() => bulkMarkDone("resolved")}
                  data-testid="recon-bulk-done"
                >
                  <CheckCircle2 size={14} className="mr-1" />Mark done ({checked.size})
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bulkBusy}
                  onClick={() => bulkMarkDone("ignored")}
                  data-testid="recon-bulk-ignore"
                >
                  Ignore
                </Button>
              </>
            )}
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="recon-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="under_review">Under review</SelectItem>
                <SelectItem value="resolved">Done</SelectItem>
                <SelectItem value="ignored">Ignored</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.severity} onValueChange={(v) => setFilters({ ...filters, severity: v })}>
              <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="recon-filter-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severity</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.sort_by} onValueChange={(v) => setFilters({ ...filters, sort_by: v })}>
              <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="recon-sort">
                <ArrowUpDown size={12} className="mr-2" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent_activity">Recent activity (sales first)</SelectItem>
                <SelectItem value="recent_sales">Recent sales only</SelectItem>
                <SelectItem value="recent_leads">Recent leads w/ appts</SelectItem>
                <SelectItem value="severity">Severity</SelectItem>
                <SelectItem value="newest">Newest detected</SelectItem>
                <SelectItem value="oldest">Oldest detected</SelectItem>
                <SelectItem value="type">Type</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={allChecked}
                      onCheckedChange={toggleAll}
                      data-testid="recon-select-all"
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead className="w-[90px]">Activity</TableHead>
                  <TableHead className="w-[90px]">Date</TableHead>
                  <TableHead className="w-[90px]">Severity</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Rep</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="w-[110px]">Status</TableHead>
                  <TableHead className="w-[140px] text-right">Quick</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => {
                  const side = d.fr || d.ghl || {};
                  const isChecked = checked.has(d.id);
                  return (
                    <TableRow
                      key={d.id}
                      className="row-hover"
                      data-testid={`recon-row-${d.id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleOne(d.id)}
                          data-testid={`recon-check-${d.id}`}
                        />
                      </TableCell>
                      <TableCell><ActivityBadge kind={d.activity_kind} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatActivityDate(d.activity_date || d.detected_at)}</TableCell>
                      <TableCell><SeverityPill level={d.severity} /></TableCell>
                      <TableCell className="text-xs capitalize cursor-pointer" onClick={() => setSelected(d)}>
                        {d.type.replaceAll("_", " ")}
                      </TableCell>
                      <TableCell className="text-sm cursor-pointer" onClick={() => setSelected(d)}>{side.name || "–"}</TableCell>
                      <TableCell className="text-xs cursor-pointer" onClick={() => setSelected(d)}>{side.rep || "–"}</TableCell>
                      <TableCell className="text-xs tabular-nums text-right">{side.value ? formatMoney(side.value) : "–"}</TableCell>
                      <TableCell><StatusPill status={d.status} /></TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); markDoneVerified(d.id); }}
                          data-testid={`recon-done-${d.id}`}
                          title="Verifies on GHL + FieldRoutes first, then marks done"
                        >
                          <CheckCircle2 size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); setSelected(d); }}
                          data-testid={`recon-open-${d.id}`}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!items.length && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-10 text-muted-foreground">
                      Nothing in this window. Nice work — try broadening the date range if you expect more.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[720px] overflow-y-auto bg-[hsl(var(--surface-1))]">
          {selected && (
            <div className="space-y-5">
              <SheetHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <SeverityPill level={selected.severity} />
                  <StatusPill status={selected.status} />
                  <ActivityBadge kind={selected.activity_kind} />
                  <span className="text-[10px] text-muted-foreground">
                    {formatActivityDate(selected.activity_date || selected.detected_at)}
                  </span>
                </div>
                <SheetTitle className="font-display">{(selected.fr || selected.ghl || {}).name || selected.type}</SheetTitle>
                <div className="text-xs text-muted-foreground">{selected.suggested_action}</div>
              </SheetHeader>

              <div>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-1 px-2">
                  <div>GoHighLevel</div>
                  <div className="text-right">FieldRoutes</div>
                </div>
                <div className="space-y-1.5">
                  <DiffField label="Name" a={selected.ghl?.name} b={selected.fr?.name} />
                  <DiffField label="Phone" a={selected.ghl?.phone_raw || selected.ghl?.phone} b={selected.fr?.phone_raw || selected.fr?.phone} />
                  <DiffField label="Phone (normalized)" a={selected.ghl?.phone} b={selected.fr?.phone} />
                  <DiffField label="Email" a={selected.ghl?.email} b={selected.fr?.email} />
                  <DiffField
                    label={`Address${typeof selected.address_similarity === "number" ? ` · ${selected.address_similarity}% match` : ""}`}
                    a={selected.ghl?.address}
                    b={selected.fr?.address}
                  />
                  {(selected.fr?.city || selected.fr?.state || selected.fr?.zip) && (
                    <>
                      <DiffField label="City" a={selected.ghl?.city} b={selected.fr?.city} />
                      <DiffField label="State" a={selected.ghl?.state} b={selected.fr?.state} />
                      <DiffField label="Zip" a={selected.ghl?.zip} b={selected.fr?.zip} />
                    </>
                  )}
                  <DiffField label="Source" a={selected.ghl?.source} b={selected.fr?.source} />
                  <DiffField label="Rep" a={selected.ghl?.rep} b={selected.fr?.rep} />
                  <DiffField label="Value" a={selected.ghl?.value ? formatMoney(selected.ghl.value) : null} b={selected.fr?.value ? formatMoney(selected.fr.value) : null} />
                  <DiffField label="Status" a={selected.ghl?.status} b={selected.fr?.status} />
                </div>
              </div>

              <div className="pt-2 border-t border-border">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
                  <NotebookPen size={12} /> Notes
                </div>
                <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                  {(selected.notes || []).slice().reverse().map((n) => (
                    <div key={n.id} className="border-l-2 border-border pl-3">
                      <div className="text-[11px] text-muted-foreground">{n.author} · {new Date(n.at).toLocaleString()}</div>
                      <div className="text-xs">{n.text}</div>
                    </div>
                  ))}
                  {!(selected.notes?.length) && <div className="text-xs text-muted-foreground italic">No notes yet</div>}
                </div>
                <div className="mt-2 flex gap-2">
                  <Textarea
                    placeholder="Add a note..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    className="min-h-[44px]"
                    data-testid="recon-note-input"
                  />
                  <Button onClick={addNote} data-testid="recon-note-add">Add</Button>
                </div>
              </div>

              <div className="pt-2 border-t border-border flex flex-wrap gap-2">
                <Button size="sm" onClick={() => updateStatus(selected.id, "under_review")} data-testid="recon-mark-review">
                  Mark under review
                </Button>
                <Button size="sm" variant="outline" onClick={() => markDoneVerified(selected.id)} data-testid="recon-mark-resolved" title="Verifies on GHL + FieldRoutes first">
                  <CheckCircle2 size={14} className="mr-1" />Mark done
                </Button>
                <Button size="sm" variant="outline" onClick={() => updateStatus(selected.id, "ignored")} data-testid="recon-mark-ignored">
                  <XCircle size={14} className="mr-1" />Ignore
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
